/**
 * Email Config Form Component
 *
 * Form for creating/editing email configurations
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as TestIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';


import { EmailConfig, EmailConfigCreate, EmailConfigUpdate } from '../../types/emailProcessor';
import emailProcessorService from '../../services/emailProcessorService';
import { primaryButtonSx } from '../../theme';

interface EmailConfigFormProps {
  open: boolean;
  config: EmailConfig | null;
  onClose: (success: boolean) => void;
  onError: (error: string) => void;
}

const EmailConfigForm: React.FC<EmailConfigFormProps> = ({ open, config, onClose, onError }) => {
  
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      email_address: '',
      imap_server: 'imap.gmail.com',
      imap_port: 993,
      use_ssl: true,
      password: '',
      inbox_folder: 'INBOX',
      processed_folder: 'Processed',
      error_folder: 'Errors',
      auto_process: true,
      check_interval_minutes: 15,
      allowed_extensions: '.pdf,.png,.jpg,.jpeg,.xlsx,.xml',
      max_attachment_size_mb: 10,
    },
  });

  useEffect(() => {
    if (config) {
      reset({
        email_address: config.email_address,
        imap_server: config.imap_server,
        imap_port: config.imap_port,
        use_ssl: config.use_ssl,
        password: '', // Don't populate password for security
        inbox_folder: config.inbox_folder,
        processed_folder: config.processed_folder || '',
        error_folder: config.error_folder || '',
        auto_process: config.auto_process,
        check_interval_minutes: config.check_interval_minutes,
        allowed_extensions: config.allowed_extensions.join(','),
        max_attachment_size_mb: config.max_attachment_size_mb,
      });
    } else {
      reset({
        email_address: '',
        imap_server: 'imap.gmail.com',
        imap_port: 993,
        use_ssl: true,
        password: '',
        inbox_folder: 'INBOX',
        processed_folder: 'Processed',
        error_folder: 'Errors',
        auto_process: true,
        check_interval_minutes: 15,
        allowed_extensions: '.pdf,.png,.jpg,.jpeg,.xlsx,.xml',
        max_attachment_size_mb: 10,
      });
    }
    setTestResult(null);
  }, [config, reset, open]);

  const handleTestConnection = async () => {
    const formData = watch();
    setTesting(true);
    setTestResult(null);

    try {
      const result = await emailProcessorService.testEmailConfig({
        email_address: formData.email_address,
        imap_server: formData.imap_server,
        imap_port: formData.imap_port,
        use_ssl: formData.use_ssl,
        password: formData.password,
      });

      setTestResult({ success: result.success, message: result.message });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const onSubmit = async (data: any) => {
    setLoading(true);

    try {
      const formData = {
        email_address: data.email_address,
        imap_server: data.imap_server,
        imap_port: data.imap_port,
        use_ssl: data.use_ssl,
        password: data.password,
        inbox_folder: data.inbox_folder,
        processed_folder: data.processed_folder || null,
        error_folder: data.error_folder || null,
        auto_process: data.auto_process,
        check_interval_minutes: data.check_interval_minutes,
        allowed_extensions: data.allowed_extensions.split(',').map((ext: string) => ext.trim()),
        max_attachment_size_mb: data.max_attachment_size_mb,
      };

      if (config) {
        // Update existing config
        const updateData: EmailConfigUpdate = { ...formData };
        if (!data.password) {
          delete updateData.password; // Don't update password if not provided
        }
        await emailProcessorService.updateEmailConfig(config.id, updateData);
      } else {
        // Create new config
        await emailProcessorService.createEmailConfig(formData as EmailConfigCreate);
      }

      onClose(true);
    } catch (error: any) {
      onError(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {config
              ? "Edit Email Configuration"
              : "Add Email Configuration"}
          </Typography>
          <IconButton onClick={() => onClose(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Email Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {"Email Settings"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="email_address"
                control={control}
                rules={{ required: 'Email address is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={"Email Address"}
                    fullWidth
                    required
                    error={!!errors.email_address}
                    helperText={errors.email_address?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={8}>
              <Controller
                name="imap_server"
                control={control}
                rules={{ required: 'IMAP server is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={"IMAP Server"}
                    fullWidth
                    required
                    error={!!errors.imap_server}
                    helperText={errors.imap_server?.message || 'e.g., imap.gmail.com'}
                  />
                )}
              />
            </Grid>

            <Grid item xs={4}>
              <Controller
                name="imap_port"
                control={control}
                rules={{ required: 'Port is required', min: 1, max: 65535 }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label={"Port"}
                    fullWidth
                    required
                    error={!!errors.imap_port}
                    helperText={errors.imap_port?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="use_ssl"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label={"Use SSL/TLS"}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="password"
                control={control}
                rules={config ? {} : { required: 'Password is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="password"
                    label={"Password"}
                    fullWidth
                    required={!config}
                    error={!!errors.password}
                    helperText={
                      config
                        ? 'Leave blank to keep current password'
                        : 'Use app-specific password for Gmail'
                    }
                  />
                )}
              />
            </Grid>

            {/* Test Connection */}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={testing ? <CircularProgress size={20} /> : <TestIcon />}
                onClick={handleTestConnection}
                disabled={testing || !watch('email_address') || !watch('password')}
                fullWidth
              >
                {"Test Connection"}
              </Button>

              {testResult && (
                <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                  {testResult.message}
                </Alert>
              )}
            </Grid>

            {/* Folder Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                {"Folder Settings"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="inbox_folder"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label={"Inbox Folder"} fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="processed_folder"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={"Processed Folder"}
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="error_folder"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label={"Error Folder"} fullWidth />
                )}
              />
            </Grid>

            {/* Processing Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                {"Processing Settings"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="auto_process"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label={"Automatically process new attachments"}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="check_interval_minutes"
                control={control}
                rules={{ required: true, min: 5, max: 1440 }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label={"Check Interval (minutes)"}
                    fullWidth
                    required
                    error={!!errors.check_interval_minutes}
                    helperText={errors.check_interval_minutes?.message || 'Between 5 and 1440 minutes'}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="max_attachment_size_mb"
                control={control}
                rules={{ required: true, min: 1, max: 100 }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label={"Max Attachment Size (MB)"}
                    fullWidth
                    required
                    error={!!errors.max_attachment_size_mb}
                    helperText={errors.max_attachment_size_mb?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="allowed_extensions"
                control={control}
                rules={{ required: 'Allowed extensions are required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={"Allowed Extensions"}
                    fullWidth
                    required
                    error={!!errors.allowed_extensions}
                    helperText={errors.allowed_extensions?.message || 'Comma-separated, e.g., .pdf,.png,.jpg'}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => onClose(false)} disabled={loading}>
            {"Cancel"}
          </Button>
          <Button type="submit" variant="contained" disabled={loading} sx={primaryButtonSx}>
            {loading ? <CircularProgress size={24} /> : config ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EmailConfigForm;
